import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Grid,
  Button,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { CaretRight } from '@phosphor-icons/react';
import { Country, State, ICountry, IState } from 'country-state-city';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { BillingDetails as BillingDetailsType } from './types';
import BillingDetailsSkeleton from './BillingDetailsSkeleton';
import { parseGoogleAddressComponents } from '../../utils/billing/addressParser';
import { getGoogleLocationsGeocode } from '../../utils/apis';

interface BillingDetailsProps {
  billingDetails: BillingDetailsType;
  billingErrors: Record<string, string>;
  updateBillingDetails: (updates: Partial<BillingDetailsType>) => void;
  updateBillingAddress: (
    updates: Partial<BillingDetailsType['address']>
  ) => void;
  setBillingErrors: (errors: Record<string, string>) => void;
  loading?: boolean;
  onNext?: () => void;
  saving?: boolean;
}

const BillingDetails = ({
  billingDetails,
  billingErrors,
  updateBillingDetails,
  updateBillingAddress,
  setBillingErrors,
  loading,
  onNext,
  saving,
}: BillingDetailsProps) => {
  // State management for countries, states, and geocoding
  const [availableCountries] = useState<ICountry[]>(Country.getAllCountries());
  const [availableStates, setAvailableStates] = useState<IState[]>([]);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);

  // Populate states when country changes or on initial load
  useEffect(() => {
    if (billingDetails.address.country) {
      const selectedCountry = availableCountries.find(
        c => c.name === billingDetails.address.country
      );
      if (selectedCountry) {
        const statesForCountry = State.getStatesOfCountry(
          selectedCountry.isoCode
        );
        setAvailableStates(statesForCountry);
      }
    }
  }, [billingDetails.address.country, availableCountries]);

  const handleChange = (field: keyof BillingDetailsType, value: string) => {
    updateBillingDetails({ [field]: value });
    // Clear error for this field
    if (billingErrors[field]) {
      const newErrors = { ...billingErrors };
      delete newErrors[field];
      setBillingErrors(newErrors);
    }
  };

  const handleAddressChange = (
    field: keyof BillingDetailsType['address'],
    value: string
  ) => {
    updateBillingAddress({ [field]: value });
    // Clear error for this field
    if (billingErrors[field]) {
      const newErrors = { ...billingErrors };
      delete newErrors[field];
      setBillingErrors(newErrors);
    }
  };

  const handleCountryChange = (country: string) => {
    updateBillingAddress({ country, state: '' });

    // Find country and populate states
    const selectedCountry = availableCountries.find(c => c.name === country);
    if (selectedCountry) {
      const statesForCountry = State.getStatesOfCountry(
        selectedCountry.isoCode
      );
      setAvailableStates(statesForCountry);
    } else {
      setAvailableStates([]);
    }
  };

  // Handle postal code auto-fill
  const handlePostalCodeBlur = async () => {
    const zipCode = billingDetails.address.zipCode;

    // Only auto-fill if zip is valid and other fields are empty
    if (zipCode.length >= 4 && !billingDetails.address.city) {
      setIsGeocodingLoading(true);
      try {
        const response = await getGoogleLocationsGeocode({ query: zipCode });
        const result = response.data.results?.[0];

        if (result?.address_components) {
          const parsed = parseGoogleAddressComponents(
            result.address_components
          );

          // Only update empty fields
          updateBillingAddress({
            city: parsed.city || billingDetails.address.city,
            state: parsed.state || billingDetails.address.state,
            country: parsed.country || billingDetails.address.country,
          });

          if (parsed.country) {
            handleCountryChange(parsed.country);
          }
        }
      } catch (error) {
        console.error('Failed to geocode postal code:', error);
      } finally {
        setIsGeocodingLoading(false);
      }
    }
  };

  // Show skeleton while loading
  if (loading) {
    return <BillingDetailsSkeleton />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header without Save Button */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#2B2D3F',
            mb: 0.5,
          }}
        >
          Company Information
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '14px' }}
        >
          Enter your company and address information. Click "Next" to save and
          continue to pricing configuration.
        </Typography>
      </Box>

      {/* Company Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '16px' }}
        >
          Basic Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Company Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter company name"
                value={billingDetails.companyName}
                onChange={e => handleChange('companyName', e.target.value)}
                error={!!billingErrors.companyName}
                helperText={billingErrors.companyName}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Email *
              </Typography>
              <TextField
                fullWidth
                placeholder="billing@company.com"
                type="email"
                value={billingDetails.email}
                onChange={e => handleChange('email', e.target.value)}
                error={!!billingErrors.email}
                helperText={billingErrors.email}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Phone Number
              </Typography>
              <PhoneInput
                country="in"
                value={billingDetails.phone}
                onChange={phone => handleChange('phone', phone)}
                placeholder=""
                specialLabel=""
                enableSearch={true}
                inputProps={{
                  name: 'phone',
                  required: false,
                  autoFocus: false,
                }}
                inputStyle={{
                  width: '100%',
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  border: billingErrors.phone
                    ? '1px solid #d32f2f'
                    : '1px solid #DCE0E7',
                  fontSize: '14px',
                  color: '#302B3F',
                }}
                containerStyle={{ width: '100%' }}
                buttonStyle={{
                  borderRadius: '6px 0 0 6px',
                  border: billingErrors.phone
                    ? '1px solid #d32f2f'
                    : '1px solid #DCE0E7',
                  backgroundColor: '#F8F9FC',
                }}
              />
              {billingErrors.phone && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5, fontSize: '12px' }}
                >
                  {billingErrors.phone}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                GST Number / Tax ID
              </Typography>
              <TextField
                fullWidth
                placeholder="22AAAAA0000A1Z5"
                value={billingDetails.gstNumber}
                onChange={e => handleChange('gstNumber', e.target.value)}
                error={!!billingErrors.gstNumber}
                helperText={billingErrors.gstNumber}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Address Section */}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '16px' }}
        >
          Address
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Street Address 1 *
              </Typography>
              <TextField
                fullWidth
                placeholder="123 Main St"
                value={billingDetails.address.street1}
                onChange={e => handleAddressChange('street1', e.target.value)}
                error={!!billingErrors.street1}
                helperText={billingErrors.street1}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Street Address 2 (Optional)
              </Typography>
              <TextField
                fullWidth
                placeholder="Suite 101"
                value={billingDetails.address.street2}
                onChange={e => handleAddressChange('street2', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                City *
              </Typography>
              <TextField
                fullWidth
                placeholder="Bengaluru"
                value={billingDetails.address.city}
                onChange={e => handleAddressChange('city', e.target.value)}
                error={!!billingErrors.city}
                helperText={billingErrors.city}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                ZIP/Postal Code *
              </Typography>
              <TextField
                fullWidth
                placeholder="560001"
                value={billingDetails.address.zipCode}
                onChange={e => handleAddressChange('zipCode', e.target.value)}
                onBlur={handlePostalCodeBlur}
                disabled={isGeocodingLoading}
                error={!!billingErrors.zipCode}
                helperText={billingErrors.zipCode}
                InputProps={{
                  endAdornment: isGeocodingLoading ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Country *
              </Typography>
              <Autocomplete
                options={availableCountries}
                value={
                  availableCountries.find(
                    c => c.name === billingDetails.address.country
                  ) || null
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    handleCountryChange(newValue.name);
                  } else {
                    handleCountryChange('');
                  }
                }}
                getOptionLabel={option => option.name}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.isoCode}
                    sx={{
                      display: 'flex !important',
                      alignItems: 'center !important',
                      gap: 1.5,
                      fontSize: '14px',
                      padding: '8px 16px !important',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '24px',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: '30px',
                      }}
                    >
                      {option.flag}
                    </Box>
                    <Box component="span">{option.name}</Box>
                  </Box>
                )}
                renderInput={params => {
                  const selectedCountry = availableCountries.find(
                    c => c.name === billingDetails.address.country
                  );
                  return (
                    <TextField
                      {...params}
                      placeholder="Search country..."
                      error={!!billingErrors.country}
                      helperText={billingErrors.country}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selectedCountry ? (
                          <Box
                            sx={{
                              fontSize: '24px',
                              lineHeight: 1,
                              display: 'flex',
                              alignItems: 'center',
                              mr: 1,
                            }}
                          >
                            {selectedCountry.flag}
                          </Box>
                        ) : null,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          backgroundColor: '#FFFFFF',
                          '& fieldset': {
                            borderWidth: '1px',
                            borderColor: '#DCE0E7',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '14px',
                          color: '#302B3F',
                        },
                      }}
                    />
                  );
                }}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    padding: '4px 12px',
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                State *
              </Typography>
              <Autocomplete
                options={availableStates}
                value={
                  availableStates.find(
                    s => s.name === billingDetails.address.state
                  ) || null
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    handleAddressChange('state', newValue.name);
                  } else {
                    handleAddressChange('state', '');
                  }
                }}
                disabled={
                  !billingDetails.address.country ||
                  availableStates.length === 0
                }
                getOptionLabel={option => option.name}
                renderOption={(props, option) => (
                  <li {...props} key={option.isoCode}>
                    {option.name}
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Search state..."
                    error={!!billingErrors.state}
                    helperText={billingErrors.state}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#DCE0E7',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: '14px',
                        color: '#302B3F',
                      },
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    padding: '4px 12px',
                  },
                }}
              />
              {billingErrors.state && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {billingErrors.state}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Next Button */}
      {onNext && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
            pt: 3,
            borderTop: '1px solid #DCE0E7',
          }}
        >
          <Button
            variant="contained"
            onClick={onNext}
            disabled={saving}
            endIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CaretRight size={16} weight="bold" />
              )
            }
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              height: '40px',
              bgcolor: '#5A4CE6',
              color: '#FFFFFF',
              boxShadow: 'none',
              fontSize: '14px',
              fontWeight: 500,
              px: 3,
              '&:hover': {
                bgcolor: '#4A3CD6',
                boxShadow: 'none',
              },
            }}
          >
            {saving ? 'Saving...' : 'Next: Configure Pricing'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BillingDetails;
